/**
 * Block-based email template editor (iter244 Phase 2).
 *
 * Stores content as a JSON array of typed blocks; renders to HTML on
 * save via `blocksToHtml(blocks)`. This is simpler than a full
 * drag-and-drop builder but covers the user's stated needs: Hero,
 * Heading, Paragraph, Image, Button, 2-column, Divider, Footer, Video.
 *
 * Image uploads go directly to Cloudinary using a signed payload we
 * fetch from our backend (API secret stays on the server).
 */
import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';
import {
  ArrowUp, ArrowDown, Trash2, Plus, Image as ImageIcon, Type, Heading as HeadingIcon,
  AlignLeft, Square, Columns, Minus, Video, Loader2,
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const BLOCK_DEFAULTS = {
  hero: {
    type: 'hero',
    image_url: '',
    heading: 'Big Headline Here',
    subheading: 'Subheading or tagline',
    button_text: 'Learn More',
    button_url: 'https://delhicapitalsacademynj.com',
    background: '#0f172a',
    text_color: '#ffffff',
  },
  heading: { type: 'heading', text: 'Section Title', level: 'h2', align: 'left', color: '#0f172a' },
  paragraph: { type: 'paragraph', html: '<p>Write your message here. <strong>Bold</strong>, <em>italic</em>, and <a href="https://example.com">links</a> are supported.</p>', line_height: 1.6 },
  image: { type: 'image', image_url: '', alt: '', link_url: '', width: 600, align: 'center' },
  button: { type: 'button', text: 'Click Me', url: 'https://delhicapitalsacademynj.com', color: '#ffffff', background: '#0f172a', align: 'center' },
  columns: {
    type: 'columns',
    left: { html: '<p><strong>Left column</strong> content goes here.</p>' },
    right: { html: '<p><strong>Right column</strong> content goes here.</p>' },
  },
  divider: { type: 'divider', color: '#e5e7eb', thickness: 1, margin: 24 },
  video: { type: 'video', video_url: '', thumbnail_url: '', platform: '', overlay: true },
  footer: { type: 'footer', html: '<p style="font-size:12px;color:#6b7280;">Delhi Capitals Cricket Academy NJ · 3775 Park Av, Unit 8B, Edison NJ 08844</p>' },
};

const PALETTE = [
  { key: 'hero', icon: Square, label: 'Hero' },
  { key: 'heading', icon: HeadingIcon, label: 'Heading' },
  { key: 'paragraph', icon: AlignLeft, label: 'Paragraph' },
  { key: 'image', icon: ImageIcon, label: 'Image' },
  { key: 'button', icon: Square, label: 'Button' },
  { key: 'columns', icon: Columns, label: '2-column' },
  { key: 'video', icon: Video, label: 'Video' },
  { key: 'divider', icon: Minus, label: 'Divider' },
  { key: 'footer', icon: Type, label: 'Footer' },
];

// -------- HTML rendering --------
const _escape = (s = '') => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function renderBlock(b) {
  switch (b.type) {
    case 'hero':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 16px;background:${b.background || '#0f172a'};color:${b.text_color || '#fff'};border-radius:8px;overflow:hidden;">
  <tr><td style="padding:36px 32px;text-align:center;font-family:Arial,Helvetica,sans-serif;">
    ${b.image_url ? `<img src="${_escape(b.image_url)}" alt="" style="max-width:100%;height:auto;border-radius:6px;margin-bottom:16px;" />` : ''}
    <h1 style="margin:0 0 8px;font-size:28px;line-height:1.2;">${_escape(b.heading || '')}</h1>
    ${b.subheading ? `<p style="margin:0 0 20px;opacity:.85;font-size:16px;">${_escape(b.subheading)}</p>` : ''}
    ${b.button_text ? `<a href="${_escape(b.button_url || '#')}" style="display:inline-block;background:#fff;color:${b.background || '#0f172a'};padding:10px 22px;border-radius:6px;text-decoration:none;font-weight:600;">${_escape(b.button_text)}</a>` : ''}
  </td></tr></table>`;
    case 'heading': {
      const tag = ['h1', 'h2', 'h3'].includes(b.level) ? b.level : 'h2';
      return `<${tag} style="margin:16px 0 8px;color:${b.color || '#0f172a'};text-align:${b.align || 'left'};font-family:Arial,Helvetica,sans-serif;">${_escape(b.text || '')}</${tag}>`;
    }
    case 'paragraph':
      return `<div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;color:#1f2937;line-height:${b.line_height || 1.6};margin:0 0 16px;">${b.html || ''}</div>`;
    case 'image':
      if (!b.image_url) return '';
      // eslint-disable-next-line no-case-declarations
      const img = `<img src="${_escape(b.image_url)}" alt="${_escape(b.alt || '')}" width="${b.width || 600}" style="display:block;max-width:100%;height:auto;border-radius:4px;margin:0 auto;" />`;
      return `<div style="text-align:${b.align || 'center'};margin:0 0 16px;">${b.link_url ? `<a href="${_escape(b.link_url)}">${img}</a>` : img}</div>`;
    case 'button':
      return `<div style="text-align:${b.align || 'center'};margin:16px 0;">
  <a href="${_escape(b.url || '#')}" style="display:inline-block;background:${b.background || '#0f172a'};color:${b.color || '#fff'};padding:12px 28px;border-radius:6px;text-decoration:none;font-weight:600;font-family:Arial,Helvetica,sans-serif;font-size:15px;">${_escape(b.text || 'Click')}</a>
</div>`;
    case 'columns':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 16px;font-family:Arial,Helvetica,sans-serif;">
  <tr>
    <td valign="top" style="width:50%;padding:0 8px 0 0;font-size:14px;color:#1f2937;line-height:1.6;">${b.left?.html || ''}</td>
    <td valign="top" style="width:50%;padding:0 0 0 8px;font-size:14px;color:#1f2937;line-height:1.6;">${b.right?.html || ''}</td>
  </tr></table>`;
    case 'divider':
      return `<div style="margin:${b.margin || 24}px 0;border-top:${b.thickness || 1}px solid ${b.color || '#e5e7eb'};"></div>`;
    case 'video':
      if (!b.thumbnail_url) return '';
      // eslint-disable-next-line no-case-declarations
      const playOverlay = b.overlay
        ? `<div style="position:relative;display:inline-block;"><img src="${_escape(b.thumbnail_url)}" alt="Watch video" style="display:block;max-width:100%;border-radius:6px;" />
<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:rgba(0,0,0,.65);color:#fff;border-radius:50%;width:60px;height:60px;line-height:60px;text-align:center;font-size:28px;">&#9658;</div></div>`
        : `<img src="${_escape(b.thumbnail_url)}" alt="Watch video" style="display:block;max-width:100%;border-radius:6px;" />`;
      return `<div style="text-align:center;margin:0 0 16px;"><a href="${_escape(b.video_url || '#')}">${playOverlay}</a></div>`;
    case 'footer':
      return `<div style="margin-top:24px;padding-top:16px;border-top:1px solid #e5e7eb;font-family:Arial,Helvetica,sans-serif;">${b.html || ''}</div>`;
    default:
      return '';
  }
}

export function blocksToHtml(blocks = []) {
  const inner = blocks.map(renderBlock).join('\n');
  return `<div style="max-width:600px;margin:0 auto;padding:20px;background:#fafafa;">
  <div style="background:#ffffff;padding:24px;border-radius:8px;">
${inner}
  </div>
</div>`;
}

// -------- Editor UI --------

export default function BlockEmailEditor({ blocks, onChange }) {
  const [selectedIdx, setSelectedIdx] = useState(null);
  const fileInputRef = useRef(null);
  const [pendingImageBlockIdx, setPendingImageBlockIdx] = useState(null);
  const [pendingImageField, setPendingImageField] = useState('image_url');
  const [uploading, setUploading] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);

  const setBlocks = (b) => onChange(b);
  const updateBlock = (i, patch) => setBlocks(blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const updateBlockNested = (i, key, patch) => setBlocks(blocks.map((b, idx) => idx === i ? { ...b, [key]: { ...(b[key] || {}), ...patch } } : b));
  const moveBlock = (i, dir) => {
    const ni = i + dir;
    if (ni < 0 || ni >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[ni]] = [next[ni], next[i]];
    setBlocks(next);
    setSelectedIdx(ni);
  };
  const deleteBlock = (i) => {
    setBlocks(blocks.filter((_, idx) => idx !== i));
    setSelectedIdx(null);
  };
  const addBlock = (key) => {
    setBlocks([...blocks, { ...BLOCK_DEFAULTS[key] }]);
    setSelectedIdx(blocks.length);
  };

  const triggerUpload = (idx, field = 'image_url') => {
    setPendingImageBlockIdx(idx);
    setPendingImageField(field);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || pendingImageBlockIdx === null) return;
    setUploading(true);
    try {
      const { data: sig } = await api.get('/cloudinary/signature?resource_type=image&folder=email-campaigns');
      const form = new FormData();
      form.append('file', file);
      form.append('api_key', sig.api_key);
      form.append('timestamp', sig.timestamp);
      form.append('signature', sig.signature);
      form.append('folder', sig.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: 'POST', body: form });
      const data = await res.json();
      if (!data.secure_url) throw new Error(data.error?.message || 'Upload failed');
      updateBlock(pendingImageBlockIdx, { [pendingImageField]: data.secure_url });
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Image upload failed');
    } finally {
      setUploading(false);
      setPendingImageBlockIdx(null);
    }
  };

  const fetchVideoMeta = async (idx, url) => {
    if (!url) return;
    setVideoLoading(true);
    try {
      const { data } = await api.get(`/email/video-meta?url=${encodeURIComponent(url)}`);
      updateBlock(idx, {
        video_url: data.embed_url,
        thumbnail_url: data.thumbnail_url_hd || data.thumbnail_url,
        platform: data.platform,
      });
      toast.success(`${data.platform} thumbnail loaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not fetch video meta');
    } finally {
      setVideoLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-12 gap-3" data-testid="block-editor">
      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileChange} />

      {/* Palette */}
      <div className="col-span-3">
        <Card className="p-3 bg-slate-50">
          <p className="text-xs font-medium text-slate-500 mb-2">Add block</p>
          <div className="grid grid-cols-2 gap-1.5">
            {PALETTE.map(({ key, icon: Icon, label }) => (
              <Button key={key} variant="outline" size="sm" className="justify-start text-xs" onClick={() => addBlock(key)} data-testid={`add-block-${key}`}>
                <Icon className="h-3 w-3 mr-1" />{label}
              </Button>
            ))}
          </div>
        </Card>
      </div>

      {/* Block list / preview */}
      <div className="col-span-5">
        <Card className="p-3 min-h-[400px]">
          {blocks.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No blocks yet. Click a block type on the left to add one.</div>
          ) : (
            <div className="space-y-2">
              {blocks.map((b, i) => (
                <div
                  key={i}
                  className={`p-2 border rounded cursor-pointer flex items-start gap-2 text-xs ${selectedIdx === i ? 'border-blue-500 bg-blue-50' : 'hover:bg-slate-50'}`}
                  onClick={() => setSelectedIdx(i)}
                  data-testid={`block-row-${i}`}
                >
                  <span className="font-mono text-slate-400 mt-0.5">{i + 1}.</span>
                  <div className="flex-1 truncate">
                    <p className="font-medium capitalize">{b.type}</p>
                    <p className="text-slate-500 truncate">{
                      b.type === 'hero' ? b.heading :
                      b.type === 'heading' ? b.text :
                      b.type === 'paragraph' ? (b.html || '').replace(/<[^>]+>/g, '').slice(0, 60) :
                      b.type === 'image' ? (b.image_url ? '✓ image set' : 'no image') :
                      b.type === 'button' ? b.text :
                      b.type === 'video' ? (b.thumbnail_url ? '✓ video set' : b.video_url || 'no video') :
                      b.type === 'divider' ? `${b.thickness}px ${b.color}` :
                      b.type === 'columns' ? '2 columns' :
                      b.type === 'footer' ? 'Footer' : ''
                    }</p>
                  </div>
                  <div className="flex flex-col">
                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={(e) => { e.stopPropagation(); moveBlock(i, -1); }} data-testid={`move-up-${i}`}><ArrowUp className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={(e) => { e.stopPropagation(); moveBlock(i, 1); }} data-testid={`move-down-${i}`}><ArrowDown className="h-3 w-3" /></Button>
                  </div>
                  <Button variant="ghost" size="icon" className="h-5 w-5" onClick={(e) => { e.stopPropagation(); deleteBlock(i); }} data-testid={`delete-block-${i}`}><Trash2 className="h-3 w-3 text-rose-600" /></Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Block inspector */}
      <div className="col-span-4">
        <Card className="p-3 min-h-[400px]">
          {selectedIdx === null || !blocks[selectedIdx] ? (
            <p className="text-xs text-slate-400 text-center py-12">Select a block to edit its properties.</p>
          ) : (
            <BlockInspector
              block={blocks[selectedIdx]}
              onUpdate={(patch) => updateBlock(selectedIdx, patch)}
              onUpdateNested={(k, p) => updateBlockNested(selectedIdx, k, p)}
              onUploadImage={(field) => triggerUpload(selectedIdx, field)}
              onFetchVideo={(url) => fetchVideoMeta(selectedIdx, url)}
              uploading={uploading}
              videoLoading={videoLoading}
            />
          )}
        </Card>
      </div>
    </div>
  );
}

function BlockInspector({ block, onUpdate, onUpdateNested, onUploadImage, onFetchVideo, uploading, videoLoading }) {
  const t = block.type;
  if (t === 'hero') return (
    <div className="space-y-2 text-xs">
      <p className="font-semibold mb-2">Hero Block</p>
      <Label>Heading</Label><Input value={block.heading || ''} onChange={(e) => onUpdate({ heading: e.target.value })} />
      <Label>Subheading</Label><Input value={block.subheading || ''} onChange={(e) => onUpdate({ subheading: e.target.value })} />
      <Label>Hero image</Label>
      {block.image_url && <img src={block.image_url} alt="" className="w-full max-h-32 object-cover rounded" />}
      <Button variant="outline" size="sm" onClick={() => onUploadImage('image_url')} disabled={uploading} className="w-full">{uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}{block.image_url ? 'Replace image' : 'Upload image'}</Button>
      <Label>Button text</Label><Input value={block.button_text || ''} onChange={(e) => onUpdate({ button_text: e.target.value })} />
      <Label>Button URL</Label><Input value={block.button_url || ''} onChange={(e) => onUpdate({ button_url: e.target.value })} />
      <div className="grid grid-cols-2 gap-2">
        <div><Label>Background</Label><Input type="color" value={block.background || '#0f172a'} onChange={(e) => onUpdate({ background: e.target.value })} /></div>
        <div><Label>Text</Label><Input type="color" value={block.text_color || '#ffffff'} onChange={(e) => onUpdate({ text_color: e.target.value })} /></div>
      </div>
    </div>
  );
  if (t === 'heading') return (
    <div className="space-y-2 text-xs">
      <p className="font-semibold mb-2">Heading</p>
      <Label>Text</Label><Input value={block.text || ''} onChange={(e) => onUpdate({ text: e.target.value })} />
      <Label>Level</Label>
      <select value={block.level} onChange={(e) => onUpdate({ level: e.target.value })} className="w-full border rounded px-2 py-1 text-xs">
        <option value="h1">H1</option><option value="h2">H2</option><option value="h3">H3</option>
      </select>
      <Label>Align</Label>
      <select value={block.align} onChange={(e) => onUpdate({ align: e.target.value })} className="w-full border rounded px-2 py-1 text-xs">
        <option>left</option><option>center</option><option>right</option>
      </select>
      <Label>Color</Label><Input type="color" value={block.color || '#0f172a'} onChange={(e) => onUpdate({ color: e.target.value })} />
    </div>
  );
  if (t === 'paragraph') return (
    <div className="space-y-2 text-xs">
      <p className="font-semibold mb-2">Paragraph</p>
      <Label>HTML (inline tags supported)</Label>
      <Textarea rows={8} value={block.html || ''} onChange={(e) => onUpdate({ html: e.target.value })} className="font-mono text-xs" />
      <Label>Line height ({block.line_height || 1.6})</Label>
      <input type="range" min="1" max="2.4" step="0.05" value={block.line_height || 1.6} onChange={(e) => onUpdate({ line_height: parseFloat(e.target.value) })} className="w-full" />
    </div>
  );
  if (t === 'image') return (
    <div className="space-y-2 text-xs">
      <p className="font-semibold mb-2">Image</p>
      {block.image_url && <img src={block.image_url} alt="" className="w-full max-h-40 object-contain rounded border" />}
      <Button variant="outline" size="sm" onClick={() => onUploadImage('image_url')} disabled={uploading} className="w-full">{uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}{block.image_url ? 'Replace image' : 'Upload image'}</Button>
      <Label>Alt text</Label><Input value={block.alt || ''} onChange={(e) => onUpdate({ alt: e.target.value })} />
      <Label>Link URL (optional)</Label><Input value={block.link_url || ''} onChange={(e) => onUpdate({ link_url: e.target.value })} />
      <Label>Max width (px)</Label><Input type="number" value={block.width || 600} onChange={(e) => onUpdate({ width: parseInt(e.target.value || 600, 10) })} />
    </div>
  );
  if (t === 'button') return (
    <div className="space-y-2 text-xs">
      <p className="font-semibold mb-2">Button</p>
      <Label>Text</Label><Input value={block.text || ''} onChange={(e) => onUpdate({ text: e.target.value })} />
      <Label>URL</Label><Input value={block.url || ''} onChange={(e) => onUpdate({ url: e.target.value })} />
      <div className="grid grid-cols-2 gap-2">
        <div><Label>Background</Label><Input type="color" value={block.background || '#0f172a'} onChange={(e) => onUpdate({ background: e.target.value })} /></div>
        <div><Label>Text color</Label><Input type="color" value={block.color || '#ffffff'} onChange={(e) => onUpdate({ color: e.target.value })} /></div>
      </div>
      <Label>Align</Label>
      <select value={block.align} onChange={(e) => onUpdate({ align: e.target.value })} className="w-full border rounded px-2 py-1 text-xs">
        <option>left</option><option>center</option><option>right</option>
      </select>
    </div>
  );
  if (t === 'columns') return (
    <div className="space-y-2 text-xs">
      <p className="font-semibold mb-2">2-Column</p>
      <Label>Left HTML</Label>
      <Textarea rows={5} value={block.left?.html || ''} onChange={(e) => onUpdateNested('left', { html: e.target.value })} className="font-mono text-xs" />
      <Label>Right HTML</Label>
      <Textarea rows={5} value={block.right?.html || ''} onChange={(e) => onUpdateNested('right', { html: e.target.value })} className="font-mono text-xs" />
    </div>
  );
  if (t === 'video') return (
    <div className="space-y-2 text-xs">
      <p className="font-semibold mb-2">Video (YouTube / Vimeo)</p>
      <Label>Video URL</Label>
      <Input value={block.video_url || ''} onChange={(e) => onUpdate({ video_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." />
      <Button variant="outline" size="sm" onClick={() => onFetchVideo(block.video_url)} disabled={videoLoading} className="w-full">{videoLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Video className="h-3 w-3 mr-1" />}Fetch thumbnail</Button>
      {block.thumbnail_url && <img src={block.thumbnail_url} alt="" className="w-full max-h-32 object-cover rounded border mt-1" />}
      <label className="flex items-center gap-2 mt-1"><input type="checkbox" checked={block.overlay !== false} onChange={(e) => onUpdate({ overlay: e.target.checked })} />Show play-button overlay</label>
    </div>
  );
  if (t === 'divider') return (
    <div className="space-y-2 text-xs">
      <p className="font-semibold mb-2">Divider</p>
      <Label>Color</Label><Input type="color" value={block.color || '#e5e7eb'} onChange={(e) => onUpdate({ color: e.target.value })} />
      <Label>Thickness (px)</Label><Input type="number" value={block.thickness || 1} onChange={(e) => onUpdate({ thickness: parseInt(e.target.value || 1, 10) })} />
      <Label>Vertical margin (px)</Label><Input type="number" value={block.margin || 24} onChange={(e) => onUpdate({ margin: parseInt(e.target.value || 24, 10) })} />
    </div>
  );
  if (t === 'footer') return (
    <div className="space-y-2 text-xs">
      <p className="font-semibold mb-2">Custom Footer</p>
      <Label>HTML</Label>
      <Textarea rows={6} value={block.html || ''} onChange={(e) => onUpdate({ html: e.target.value })} className="font-mono text-xs" />
      <p className="text-slate-400 italic">Note: the CAN-SPAM unsubscribe footer is automatically appended at send time — this block is for additional branding/links.</p>
    </div>
  );
  return null;
}
